// src/services/apiServices.ts — UNIFIED PLATFORM API & AI SERVICE ENGINE
// Consolidated single source of truth combining client API proxies and server-side Gemini intelligence.

import { GoogleGenAI, Type, ThinkingLevel } from "@google/genai";
import {
  FeatureType,
  AnalysisResult,
  LocationSuggestion,
  BusinessIdea,
  ComparisonAnalysis,
  RiskGuardDeepDive,
  LaunchMathDeepDive,
  SiteReconDeepDive,
  ScalingStrategyDeepDive,
  IntelligenceHubData,
  OperationsHubData,
  CollaborationHubData
} from '../types';
import { generateVentureId } from '../utils/formatters';

// Detect runtime environment
export const isServer = typeof window === 'undefined';

// ==========================================
// 1. CLIENT-SIDE API GATEWAY & AUTHENTICATION
// ==========================================

const API_BASE = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_BASE_URL) || '';

export async function getAuthToken(): Promise<string | undefined> {
  if (isServer) return undefined;
  try {
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    if (auth.currentUser) {
      return await auth.currentUser.getIdToken();
    }
  } catch (e) {
    console.error('getAuthToken error:', e);
  }
  return undefined;
}

export async function getUserTier(): Promise<string> {
  if (isServer) return 'free';
  try {
    const stored = localStorage.getItem('envesta-tier');
    if (stored) return stored;
  } catch { }

  try {
    const { getAuth } = await import('firebase/auth');
    const auth = getAuth();
    if (auth.currentUser) {
      const token = await auth.currentUser.getIdToken();
      const payload = JSON.parse(atob(token.split('.')[1]));
      const tier = (payload.subscriptionTier || payload.tier || 'free').toString().toLowerCase();
      localStorage.setItem('envesta-tier', tier);
      return tier;
    }
  } catch (e) {
    console.error('getUserTier fallback error:', e);
  }

  return 'free';
}

export async function getHeaders(): Promise<Record<string, string>> {
  const token = await getAuthToken();
  const tier = await getUserTier();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-User-Id': 'anonymous',
    'X-User-Tier': tier,
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  if (!isServer && typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV) {
    const devTier = localStorage.getItem('envesta-dev-tier');
    if (devTier) headers['X-Dev-Tier'] = devTier;
  }

  return headers;
}

export async function post<T = any>(endpoint: string, body: unknown): Promise<T> {
  const headers = await getHeaders();
  const res = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

// ==========================================
// 2. CLIENT DATA TYPES & INTERFACES
// ==========================================

export interface AIMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
}

export interface GeminiRequest {
  model?: string;
  contents: { role: string; parts: { text: string }[] }[];
  config?: { maxOutputTokens?: number };
}

export interface GeminiResponse {
  text: string;
  candidates: any[];
  usage: { promptTokenCount: number; candidatesTokenCount: number; totalTokenCount: number };
}

export interface SearchGroundingResponse {
  query: string;
  results: Array<{ title: string; url: string; description: string }>;
  synthesis?: string;
  sources: string[];
}

export interface MapsPrediction {
  description: string;
  placeId: string;
}

export interface MapsLocation {
  address: string;
  lat: number;
  lng: number;
  placeId: string;
}

export interface MapsQuotaCategory {
  used: number;
  limit: number;
  remaining: number;
}

export interface MapsQuotaStatus {
  tier: string;
  billingPeriod: string;
  categories: {
    dynamicMaps: MapsQuotaCategory;
    geocoding: MapsQuotaCategory;
    autocomplete: MapsQuotaCategory;
    distanceMatrix: MapsQuotaCategory;
    staticMaps: MapsQuotaCategory;
  };
}

export interface GrassrootsInput {
  location: string;
  surroundingsDescription: string;
  availableResources: string;
  communityGaps: string;
  budgetLimit?: number;
  preferredCurrency?: string;
}

// ==========================================
// 3. MAPS & CLIENT UTILITIES
// ==========================================

export async function getMapsQuota(): Promise<MapsQuotaStatus> {
  const headers = await getHeaders();
  const res = await fetch(`${API_BASE}/api/maps/quota`, { method: 'GET', headers });
  if (!res.ok) {
    return {
      tier: 'free',
      billingPeriod: new Date().toISOString().slice(0, 7),
      categories: {
        dynamicMaps: { used: 0, limit: 5, remaining: 5 },
        geocoding: { used: 0, limit: 2, remaining: 2 },
        autocomplete: { used: 0, limit: 10, remaining: 10 },
        distanceMatrix: { used: 0, limit: 0, remaining: 0 },
        staticMaps: { used: 0, limit: 0, remaining: 0 }
      }
    };
  }
  return res.json();
}

export async function trackMapLoad(): Promise<{ allowed: boolean; remaining: number }> {
  try {
    return await post('/api/maps/track-load', {});
  } catch (err: any) {
    if (err?.message?.includes('quota limit reached') || err?.message?.includes('429')) {
      return { allowed: false, remaining: 0 };
    }
    return { allowed: true, remaining: 1 };
  }
}

export async function callGemini(request: GeminiRequest): Promise<GeminiResponse> {
  return post('/api/gemini/generate', request);
}

export async function callGeminiBatch(requests: GeminiRequest[]): Promise<{ results: (GeminiResponse | { error: string })[] }> {
  return post('/api/gemini/batch', { requests });
}

export const aiHubs = {
  launchpad: (messages: AIMessage[]) => callGemini({
    contents: messages.map(m => ({ role: m.role === 'user' ? 'user' : 'model', parts: [{ text: m.content }] })),
    config: { maxOutputTokens: 4096 }
  }),
  discovery: (messages: AIMessage[]) => callGemini({
    contents: messages.map(m => ({ role: m.role === 'user' ? 'user' : 'model', parts: [{ text: m.content }] })),
    config: { maxOutputTokens: 4096 }
  }),
  optimize: (messages: AIMessage[]) => callGemini({
    contents: messages.map(m => ({ role: m.role === 'user' ? 'user' : 'model', parts: [{ text: m.content }] })),
    config: { maxOutputTokens: 4096 }
  }),
  scrutiny: (messages: AIMessage[]) => callGemini({
    contents: messages.map(m => ({ role: m.role === 'user' ? 'user' : 'model', parts: [{ text: m.content }] })),
    config: { maxOutputTokens: 8192 }
  }),
  warRoom: (messages: AIMessage[]) => callGemini({
    contents: messages.map(m => ({ role: m.role === 'user' ? 'user' : 'model', parts: [{ text: m.content }] })),
    config: { maxOutputTokens: 4096 }
  }),
};

export async function chatWithAI(message: string, _hub: string = 'discovery', history: AIMessage[] = []): Promise<string> {
  const messages: AIMessage[] = [...history, { role: 'user', content: message }];
  const response = await callGemini({
    contents: messages.map(m => ({ role: m.role === 'user' ? 'user' : 'model', parts: [{ text: m.content }] })),
    config: { maxOutputTokens: 4096 }
  });
  return response.text;
}

export async function searchGrounding(query: string, synthesize: boolean = true): Promise<SearchGroundingResponse> {
  return post('/api/search/ground', { query, synthesize });
}

export async function mapsAutocomplete(input: string, country?: string): Promise<{ predictions: MapsPrediction[] }> {
  return post('/api/maps/autocomplete', { input, country });
}

export async function mapsGeocode(address: string): Promise<MapsLocation> {
  return post('/api/maps/geocode', { address });
}

export async function mapsPlaceDetails(placeId: string): Promise<MapsLocation> {
  return post('/api/maps/place', { placeId });
}

export async function runScan(
  featureType: string,
  location: string,
  coords: { lat: number; lng: number } | null,
  sector?: string,
  country?: string,
  groundingSynthesis?: string,
  preferredCurrency: string = 'USD'
): Promise<any> {
  const body: Record<string, any> = {
    area: location,
    sector: sector || '',
    coords,
    featureType,
    groundingSynthesis,
    preferredCurrency
  };
  if (country) body.country = country;
  return post('/api/suggestions', body);
}

export async function runDeepDive(
  type: 'risk' | 'launch' | 'site' | 'scaling',
  idea: any,
  location: string,
  preferredCurrency?: string
): Promise<any> {
  return post(`/api/deep-dive/${type}`, { idea, location, preferredCurrency });
}

export async function runHubAnalysis(
  hub: 'intelligence' | 'operations' | 'collaboration',
  idea: any,
  location: string
): Promise<any> {
  return post(`/api/hub/${hub}`, { idea, location });
}

// ==========================================
// 4. GEMINI SCHEMAS (STRUCTURED OUTPUTS)
// ==========================================

const sourceSchema = {
  type: Type.OBJECT,
  properties: {
    title: { type: Type.STRING },
    uri: { type: Type.STRING }
  },
  required: ["title", "uri"]
};

const heatmapPointSchema = {
  type: Type.OBJECT,
  properties: {
    lat: { type: Type.NUMBER },
    lng: { type: Type.NUMBER },
    weight: { type: Type.NUMBER, description: "Density intensity from 0 to 1." },
    label: { type: Type.STRING, description: "Name of the hotspot or street." }
  },
  required: ["lat", "lng", "weight"]
};

const historicalTrendPointSchema = {
  type: Type.OBJECT,
  properties: {
    period: { type: Type.STRING, description: "e.g., '2021', '2022 Q3'" },
    intensity: { type: Type.NUMBER, description: "Normalized market interest or density (0-100)" }
  },
  required: ["period", "intensity"]
};

const financialPathSchema = {
  type: Type.OBJECT,
  properties: {
    startupCost: { type: Type.STRING },
    runway12Mo: { type: Type.STRING },
    breakEvenTimeline: { type: Type.STRING },
    scalingSteps: { type: Type.ARRAY, items: { type: Type.STRING } },
    entryStrategy: { type: Type.STRING }
  },
  required: ["startupCost", "runway12Mo", "breakEvenTimeline"]
};

const financialPathMathSchema = financialPathSchema;

const riskGuardSchema = {
  type: Type.OBJECT,
  properties: {
    score: { type: Type.NUMBER },
    financial: { type: Type.STRING },
    market: { type: Type.STRING },
    regulatory: { type: Type.STRING }
  },
  required: ["score", "financial", "market", "regulatory"]
};

const competitorComparisonSchema = {
  type: Type.OBJECT,
  properties: {
    nearestCompetitor: { type: Type.STRING },
    distance: { type: Type.STRING, description: "Estimated distance (e.g., '400m away')" },
    competitorCapacity: { type: Type.STRING, description: "Current state/load of the competitor" },
    ourAdvantage: { type: Type.STRING }
  },
  required: ["nearestCompetitor", "distance", "competitorCapacity", "ourAdvantage"]
};

const siteRecommendationSchema = {
  type: Type.OBJECT,
  properties: {
    name: { type: Type.STRING, description: "Pinpoint location name (e.g., 'Kampala Road' or 'Boulevard Building')" },
    type: { type: Type.STRING, enum: ["street", "building", "corridor", "intersection"] },
    reason: { type: Type.STRING, description: "Strategic reason for this specific pinpoint choice." }
  },
  required: ["name", "type", "reason"]
};

const spottedEntitySchema = {
  type: Type.OBJECT,
  properties: {
    name: { type: Type.STRING, description: "Official business name" },
    address: { type: Type.STRING, description: "Full street address or exact block location" },
    contact: { type: Type.STRING, description: "Phone number, website URL, or primary social handle" },
    description: { type: Type.STRING, description: "Factual description of this specific business and its role in the hotspot" }
  },
  required: ["name", "address"]
};

const intelligenceHubSchema = {
  type: Type.OBJECT,
  properties: {
    competitorDeepDive: { type: Type.STRING, description: "Pricing gaps and review sentiment analysis for the sector." },
    differentiationEngine: { type: Type.STRING, description: "Blue ocean positioning strategies against local rivals." },
    marketShareEstimation: { type: Type.STRING, description: "TAM/SAM/SOM calculations using demographic data." },
    seasonalityForecasting: { type: Type.STRING, description: "Monthly revenue fluctuations for the venture." },
    gentrificationTrajectory: { type: Type.STRING, description: "Predictive indicators of neighborhood economic shifts." },
    economicCycleResilience: { type: Type.STRING, description: "Stress-test against local downturns and disruptions." },
    personaGenerator: { type: Type.STRING, description: "Customer avatars based on local psychographics." },
    footTrafficPatterns: { type: Type.STRING, description: "Mobility data insights for target customers." },
    sentimentAnalysis: { type: Type.STRING, description: "Unmet local needs and competitor complaints." }
  },
  required: ["competitorDeepDive", "differentiationEngine", "marketShareEstimation", "seasonalityForecasting", "gentrificationTrajectory", "economicCycleResilience", "personaGenerator", "footTrafficPatterns", "sentimentAnalysis"]
};

const operationsHubSchema = {
  type: Type.OBJECT,
  properties: {
    scenarioModeling: { type: Type.STRING, description: "What-if analyses on funding, pricing, and market shifts." },
    investorMatchmaking: { type: Type.STRING, description: "Sector-specific local investor profiles." },
    grantScanner: { type: Type.STRING, description: "Tax incentives, grants, and enterprise zone benefits." },
    laborMarketAnalytics: { type: Type.STRING, description: "Wage benchmarks and talent availability for roles." },
    supplyChainMapping: { type: Type.STRING, description: "Proximity to suppliers and logistics hubs." },
    regulatoryNavigator: { type: Type.STRING, description: "Permit timelines, costs, and approval durations." },
    performanceBenchmarking: { type: Type.STRING, description: "Actuals vs AI prediction comparison framework." },
    expansionScouting: { type: Type.STRING, description: "Secondary location identification for scaling." },
    exitStrategyModeling: { type: Type.STRING, description: "Valuation projections and potential acquirers." }
  },
  required: ["scenarioModeling", "investorMatchmaking", "grantScanner", "laborMarketAnalytics", "supplyChainMapping", "regulatoryNavigator", "performanceBenchmarking", "expansionScouting", "exitStrategyModeling"]
};

const collaborationHubSchema = {
  type: Type.OBJECT,
  properties: {
    stakeholderSharing: { type: Type.STRING, description: "Secure sharing protocol for co-founders and investors." },
    versionControl: { type: Type.STRING, description: "Venture evolution tracking across iterations." },
    commentaryAnnotations: { type: Type.STRING, description: "Team collaboration and note-taking framework." },
    algorithmicTransparency: { type: Type.STRING, description: "Explanation of AI recommendation logic." },
    biasDetection: { type: Type.STRING, description: "Inequity and demographic disadvantage flags." },
    dataPrivacyControls: { type: Type.STRING, description: "Proprietary data handling and privacy measures." },
    crmConnectors: { type: Type.STRING, description: "Salesforce/HubSpot/QuickBooks export mapping." },
    erpIntegrations: { type: Type.STRING, description: "Financial and operational data sync framework." },
    apiAccess: { type: Type.STRING, description: "Custom dashboard and proprietary app integration." },
    ruralMarketOptimization: { type: Type.STRING, description: "Accuracy maintenance in sparse data areas." },
    fullLocalization: { type: Type.STRING, description: "Regional data and native-language support." },
    advancedOfflineMode: { type: Type.STRING, description: "Site visit functionality without connectivity." }
  },
  required: ["stakeholderSharing", "versionControl", "commentaryAnnotations", "algorithmicTransparency", "biasDetection", "dataPrivacyControls", "crmConnectors", "erpIntegrations", "apiAccess", "ruralMarketOptimization", "fullLocalization", "advancedOfflineMode"]
};

const riskGuardDeepDiveSchema = {
  type: Type.OBJECT,
  properties: {
    regulatoryZoning: { type: Type.STRING, description: "Deep dive into local permits, compliance costs, and potential legal hurdles. At least 300 words." },
    marketSaturation: { type: Type.STRING, description: "Identify top 3 competitors and provide a SWOT analysis for each. At least 300 words." },
    operationalSupplyChain: { type: Type.STRING, description: "Map out potential failures in local logistics or staffing. At least 300 words." },
    financialStressTest: {
      type: Type.OBJECT,
      properties: {
        bestCase: { type: Type.STRING, description: "Best-case scenario regarding cash flow and burn rate. At least 100 words." },
        expectedCase: { type: Type.STRING, description: "Expected-case scenario regarding cash flow and burn rate. At least 100 words." },
        worstCase: { type: Type.STRING, description: "Worst-case scenario regarding cash flow and burn rate. At least 100 words." }
      },
      required: ["bestCase", "expectedCase", "worstCase"]
    },
    mitigationRoadmap: { type: Type.STRING, description: "Specific, actionable steps to neutralize every risk mentioned above. At least 300 words." }
  },
  required: ["regulatoryZoning", "marketSaturation", "operationalSupplyChain", "financialStressTest", "mitigationRoadmap"]
};

const launchMathDeepDiveSchema = {
  type: Type.OBJECT,
  properties: {
    startupCapital: { type: Type.STRING, description: "Detailed breakdown of renovation, equipment, licensing, and 'Safety Net' cash. At least 300 words." },
    monthlyOpEx: { type: Type.STRING, description: "Granular rent, utilities, payroll, and marketing spend for the first 12 months. At least 300 words." },
    revenueStreams: { type: Type.STRING, description: "3 distinct pricing models for the industry in the location. At least 300 words." },
    breakEvenAnalysis: { type: Type.STRING, description: "A month-by-month projection of when the venture becomes self-sustaining. At least 300 words." }
  },
  required: ["startupCapital", "monthlyOpEx", "revenueStreams", "breakEvenAnalysis"]
};

const siteReconDeepDiveSchema = {
  type: Type.OBJECT,
  properties: {
    microLocationFit: { type: Type.STRING, description: "Analyze the specific foot traffic and 'Anchor Businesses' in the district that will drive customers. At least 300 words." },
    accessibilityLogistics: { type: Type.STRING, description: "Detailed look at parking, public transit proximity, and delivery ease for suppliers. At least 300 words." },
    demographicAlignment: { type: Type.STRING, description: "Match the local resident profile (income level, age, habits) to the business model. At least 300 words." },
    visualLandscape: { type: Type.STRING, description: "Describe the 'vibe' of the street and why this specific aesthetic fits the brand. At least 300 words." }
  },
  required: ["microLocationFit", "accessibilityLogistics", "demographicAlignment", "visualLandscape"]
};

const scalingStrategyDeepDiveSchema = {
  type: Type.OBJECT,
  properties: {
    first90Days: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          week: { type: Type.STRING, description: "Week number or range (e.g., Week 1-2)." },
          milestone: { type: Type.STRING, description: "Milestone title." },
          howTo: { type: Type.STRING, description: "Detailed 'How-To' paragraph for this milestone." }
        },
        required: ["week", "milestone", "howTo"]
      }
    },
    quarterlyScaling: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          period: { type: Type.STRING, description: "Period (e.g., Month 6, 12, 18)." },
          goal: { type: Type.STRING, description: "Scaling goal title." },
          howTo: { type: Type.STRING, description: "Detailed 'How-To' paragraph for this goal." }
        },
        required: ["period", "goal", "howTo"]
      }
    },
    trendAnalysis: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          trend: { type: Type.STRING, description: "Disruptive technology or consumer shift title." },
          description: { type: Type.STRING, description: "Detailed description of the trend." },
          impact: { type: Type.STRING, description: "How this trend will impact the business." }
        },
        required: ["trend", "description", "impact"]
      }
    },
    pivotPoints: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          scenario: { type: Type.STRING, description: "Scenario where a pivot might be needed." },
          strategy: { type: Type.STRING, description: "Pivot strategy description." }
        },
        required: ["scenario", "strategy"]
      }
    }
  },
  required: ["first90Days", "quarterlyScaling", "trendAnalysis", "pivotPoints"]
};

const analysisSchema = {
  type: Type.OBJECT,
  properties: {
    summary: { type: Type.STRING },
    heatmapData: {
      type: Type.ARRAY,
      items: heatmapPointSchema,
      description: "A set of 10-15 geographic coordinates within the search area representing high business density for the heat map."
    },
    ideas: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          title: { type: Type.STRING },
          description: { type: Type.STRING, description: "A mandatory 2-3 sentence high-impact factual summary." },
          category: { type: Type.STRING },
          riskLevel: { type: Type.STRING, enum: ["Low", "Medium", "High"] },
          growthPotential: { type: Type.STRING, description: "A summary of why this business has growth runway in this location." },
          marketDemandScore: { type: Type.NUMBER, description: "Estimated market demand from 0-100." },
          saturationIndex: { type: Type.NUMBER, description: "Market gap indicator: 0 means no competition, 100 means fully saturated." },
          tags: { type: Type.ARRAY, items: { type: Type.STRING } },
          fullPath: financialPathMathSchema, 
          leanPath: financialPathMathSchema, 
          riskGuard: riskGuardSchema,
          competition: competitorComparisonSchema,
          siteRecommendations: { type: Type.ARRAY, items: siteRecommendationSchema, description: "Suggest 3 pinpoint locations within the search area." },
          estimatedStartupCost: { type: Type.STRING },
          potentialROI: { type: Type.STRING },
          densityInsight: { type: Type.STRING },
          locationName: { type: Type.STRING },
          culturalSignificance: { type: Type.STRING },
          temporalTrend: { type: Type.STRING },
          historicalTrendData: {
            type: Type.ARRAY,
            items: historicalTrendPointSchema,
            description: "6-8 data points representing the last 3 years of market activity or interest levels for this specific niche."
          },
          context: { type: Type.STRING },
          spottedEntities: { type: Type.ARRAY, items: spottedEntitySchema },
          sources: { type: Type.ARRAY, items: sourceSchema }
        },
        required: ["title", "description", "category"]
      }
    }
  },
  required: ["summary", "ideas"]
};

const suggestionSchema = {
  type: Type.ARRAY,
  items: {
    type: Type.OBJECT,
    properties: {
      name: { type: Type.STRING },
      type: { type: Type.STRING, enum: ["city", "neighborhood", "district"] },
      reason: { type: Type.STRING }
    },
    required: ["name", "type", "reason"]
  }
};

const swotElementSchema = {
  type: Type.OBJECT,
  properties: {
    id: { type: Type.STRING },
    text: { type: Type.STRING },
    score: { type: Type.NUMBER },
    confidence: { type: Type.NUMBER },
    source: { type: Type.STRING },
    status: { type: Type.STRING, enum: ["new", "updated", "stable"] },
    impact: { type: Type.NUMBER }
  },
  required: ["id", "text", "score", "confidence", "source", "status", "impact"]
};

const livingSwotSchema = {
  type: Type.OBJECT,
  properties: {
    strengths: { type: Type.ARRAY, items: swotElementSchema },
    weaknesses: { type: Type.ARRAY, items: swotElementSchema },
    opportunities: { type: Type.ARRAY, items: swotElementSchema },
    threats: { type: Type.ARRAY, items: swotElementSchema },
    narrative: { type: Type.STRING }
  },
  required: ["strengths", "weaknesses", "opportunities", "threats", "narrative"]
};

const scrutinySchema = {
  type: Type.OBJECT,
  properties: {
    fragilityIndex: { type: Type.NUMBER, description: "A composite score (0-100) where higher means more critical gaps." },
    confidence: { type: Type.NUMBER, description: "AI confidence score (0.0-1.0)." },
    criticalGaps: {
      type: Type.ARRAY,
      maxItems: 5,
      items: {
        type: Type.OBJECT,
        properties: {
          id: { type: Type.STRING },
          name: { type: Type.STRING },
          severity: { type: Type.STRING, enum: ["fatal", "major", "minor"] },
          userAssumption: { type: Type.STRING },
          contradictingData: { type: Type.STRING },
          quantifiedGap: { type: Type.STRING },
          source: { type: Type.STRING }
        },
        required: ["id", "name", "severity", "userAssumption", "contradictingData", "source"]
      }
    },
    hiddenAssumptions: {
      type: Type.ARRAY,
      maxItems: 5,
      items: {
        type: Type.OBJECT,
        properties: {
          id: { type: Type.STRING },
          assumption: { type: Type.STRING },
          whyUnsupported: { type: Type.STRING },
          relevantData: { type: Type.STRING }
        },
        required: ["id", "assumption", "whyUnsupported", "relevantData"]
      }
    },
    viablePivots: {
      type: Type.ARRAY,
      minItems: 3,
      maxItems: 5,
      items: {
        type: Type.OBJECT,
        properties: {
          id: { type: Type.STRING },
          description: { type: Type.STRING },
          estimatedCapital: { type: Type.STRING },
          estimatedTimeline: { type: Type.STRING },
          confidenceScore: { type: Type.NUMBER },
          eliminatesGaps: { type: Type.ARRAY, items: { type: Type.STRING } }
        },
        required: ["id", "description", "estimatedCapital", "estimatedTimeline", "confidenceScore", "eliminatesGaps"]
      }
    },
    recommendedNextStep: {
      type: Type.OBJECT,
      properties: {
        action: { type: Type.STRING },
        hubDestination: { type: Type.STRING, enum: ["launchpad", "discovery", "optimize"] },
        rationale: { type: Type.STRING }
      },
      required: ["action", "hubDestination", "rationale"]
    }
  },
  required: ["fragilityIndex", "confidence", "criticalGaps", "hiddenAssumptions", "viablePivots", "recommendedNextStep"]
};

const comparisonSchema = {
  type: Type.OBJECT,
  properties: {
    summary: { type: Type.STRING },
    gaps: { type: Type.ARRAY, items: { type: Type.STRING } },
    risks: { type: Type.ARRAY, items: { type: Type.STRING } },
    recommendation: { type: Type.STRING },
    winningVenture: { type: Type.STRING, description: "The title of the venture that is the most viable choice." },
    selectionReasoning: { type: Type.STRING, description: "A detailed explanation of why this venture was chosen over the others." },
    comparisonMatrix: {
      type: Type.ARRAY,
      items: {
        type: Type.OBJECT,
        properties: {
          feature: { type: Type.STRING, description: "The feature being compared." },
          values: { 
            type: Type.OBJECT, 
            additionalProperties: { type: Type.STRING },
            description: "A map where keys are venture titles and values are comparison points."
          }
        },
        required: ["feature", "values"]
      }
    }
  },
  required: ["summary", "gaps", "risks", "recommendation", "winningVenture", "selectionReasoning", "comparisonMatrix"]
};

// ==========================================
// 5. SERVER-SIDE GEMINI EXECUTION ENGINE
// ==========================================

function getAIClient(): GoogleGenAI {
  const apiKey = (typeof process !== 'undefined' && process.env?.GEMINI_API_KEY) || '';
  return new GoogleGenAI({ apiKey });
}

export async function serverGetLocationSuggestions(
  area: string,
  sector: string,
  country?: string
): Promise<LocationSuggestion[]> {
  const ai = getAIClient();
  const cleanCountry = country?.replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]/g, '').trim();
  const countryContext = cleanCountry && !cleanCountry.includes('Worldwide') ? ` ABSOLUTE REQUIREMENT: ONLY identify spots within ${cleanCountry}. Ensure all suggested results are geographically located inside this specific country.` : '';
  const prompt = `Act as a Hierarchical Location Intelligence Expert. Identify 4 high-potential spots (cities, districts, or neighborhoods) matching the search text "${area}" for "${sector || 'new businesses'}".${countryContext} Return JSON.`;

  try {
    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: prompt,
      config: {
        responseMimeType: "application/json",
        responseSchema: suggestionSchema
      }
    });
    return JSON.parse(response.text || '[]') as LocationSuggestion[];
  } catch (e) {
    return [];
  }
}

export async function serverResolveCountryFromCoords(lat: number, lng: number): Promise<string> {
  const ai = getAIClient();
  const response = await ai.models.generateContent({
    model: 'gemini-3-flash-preview',
    contents: `Translate coordinates ${lat}, ${lng} into a country name. Return ONLY the country name.`
  });
  return response.text?.trim() || "USA";
}

export async function serverResolveLocationFromCoords(lat: number, lng: number): Promise<string> {
  const ai = getAIClient();
  try {
    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: `What is the specific neighborhood, district, or city name for the coordinates ${lat}, ${lng}? Return ONLY the name. Be precise.`,
      config: {
        tools: [{ googleMaps: {} }],
        toolConfig: {
          retrievalConfig: {
            latLng: {
              latitude: lat,
              longitude: lng
            }
          }
        }
      },
    });
    return response.text?.trim().split('\n')[0] || "Identified Point";
  } catch (err) {
    console.error("Map resolution error:", err);
    return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
  }
}

export async function serverGeocodeLocation(query: string): Promise<{ lat: number, lng: number, name: string } | null> {
  const ai = getAIClient();
  try {
    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: `Find the coordinates for "${query}". Return only the latitude and longitude followed by the full name in this exact format: LAT, LNG | FULL_NAME. Example: 40.7128, -74.0060 | New York, NY`,
      config: {
        tools: [{ googleMaps: {} }],
      },
    });
    const text = response.text || "";
    const parts = text.split('|');
    if (parts.length >= 1) {
      const coordParts = parts[0].split(',');
      const lat = parseFloat(coordParts[0]);
      const lng = parseFloat(coordParts[1]);
      const name = parts[1]?.trim() || query;
      if (!isNaN(lat) && !isNaN(lng)) return { lat, lng, name };
    }
    return null;
  } catch (err) {
    console.error("Geocoding error:", err);
    return null;
  }
}

export async function serverCompareOpportunities(
  ideas: BusinessIdea[],
  preferredCurrency: string = 'USD'
): Promise<ComparisonAnalysis> {
  const ai = getAIClient();
  
  const ideasContext = ideas.map((idea, index) => `
    Opportunity ${index + 1}:
    Title: ${idea.title}
    Description: ${idea.description}
    Category: ${idea.category}
    Market Demand: ${idea.marketDemandScore}/100
    Saturation: ${idea.saturationIndex}/100
    Startup Cost: ${idea.estimatedStartupCost || 'N/A'}
    ROI: ${idea.potentialROI || 'N/A'}
    Risk Level: ${idea.riskLevel || 'N/A'}
  `).join('\n');

  const prompt = `Act as a Strategic War Room Analyst. Compare the following business opportunities:
  ${ideasContext}
  
  Perform a deep comparative analysis. Identify:
  1. Gaps: What is missing in one that the other has? What market gaps do they collectively address or miss?
  2. Risks: Comparative risk assessment.
  3. Recommendation: Which one is the most viable or how should they be prioritized?
  4. Winning Venture: Explicitly select ONE venture as the top choice. Provide its exact title.
  5. Selection Reasoning: Explain in detail why this venture was chosen over the rest of the ventures it was compared to.
  6. Comparison Matrix: A detailed feature-by-feature comparison.
  
  All monetary values should be in ${preferredCurrency}. Return JSON. Ensure the JSON is valid.`;

  const response = await ai.models.generateContent({
    model: "gemini-3-flash-preview",
    contents: prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: comparisonSchema,
      tools: [{ googleSearch: {} }]
    }
  });

  const text = response.text || '{}';
  try {
    return JSON.parse(text) as ComparisonAnalysis;
  } catch (err) {
    console.error("JSON Parse Error in compareOpportunities:", err, text);
    throw new Error("Failed to parse comparison analysis. The AI response was malformed.");
  }
}

export async function serverGenerateRiskGuardDeepDive(
  idea: BusinessIdea,
  location: string,
  preferredCurrency: string = 'USD'
): Promise<RiskGuardDeepDive> {
  const ai = getAIClient();
  
  const prompt = `Act as a Senior Risk Management Consultant. Perform an exhaustive Risk Guard Deep Dive for the following venture:
  Venture: ${idea.title}
  Description: ${idea.description}
  Location: ${location}
  
  MANDATORY: Produce five distinct chapters with extreme density (at least 300 words per chapter):
  1. Regulatory & Zoning Analysis: Deep dive into local permits, compliance costs, and potential legal hurdles in ${location}.
  2. Market Saturation & Competitive Counter-Moves: Identify the top 3 competitors in ${location} and provide a SWOT analysis for each.
  3. Operational & Supply Chain Vulnerabilities: Map out potential failures in local logistics or staffing.
  4. Financial Stress Test: 3 scenarios (Best, Expected, Worst-case) regarding cash flow and burn rate.
  5. Mitigation Roadmap: Specific, actionable steps to neutralize every risk mentioned above.
  
  Use Google Search grounding to find actual local crime rates, economic data, or recent business closures in that specific district to provide 'Human-Level' satisfaction.
  All monetary values MUST be in "${preferredCurrency}".
  Return JSON. Ensure the JSON is valid.`;

  const response = await ai.models.generateContent({
    model: "gemini-3-flash-preview",
    contents: prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: riskGuardDeepDiveSchema,
      tools: [{ googleSearch: {} }]
    }
  });

  const text = response.text || '{}';
  try {
    return JSON.parse(text) as RiskGuardDeepDive;
  } catch (err) {
    console.error("JSON Parse Error in generateRiskGuardDeepDive:", err, text);
    throw new Error("Failed to parse risk analysis. The AI response was malformed.");
  }
}

export async function serverGenerateLaunchMathDeepDive(
  idea: BusinessIdea,
  location: string,
  preferredCurrency: string = 'USD'
): Promise<LaunchMathDeepDive> {
  const ai = getAIClient();
  
  const prompt = `Act as a Senior Venture Architect and Financial Analyst. Perform an exhaustive Launch Math Deep Dive for the following venture:
  Venture: ${idea.title}
  Description: ${idea.description}
  Location: ${location}
  
  MANDATORY: Produce four distinct phases with extreme density (at least 300 words per phase):
  Phase 1: Startup Capital: Detailed breakdown of renovation, equipment, licensing, and 'Safety Net' cash.
  Phase 2: Monthly OpEx: Granular rent, utilities, payroll, and marketing spend for the first 12 months.
  Phase 3: Revenue Streams: 3 distinct pricing models for the ${idea.category} industry in ${location}.
  Phase 4: Break-Even Analysis: A month-by-month projection of when the venture becomes self-sustaining.
  
  STRICT CONSTRAINT: Do not use 'Estimated' as a placeholder. Use Google Search Grounding to find actual average commercial rent per sq ft in ${location} and real-world equipment costs. Every section needs 300+ words of justification for the numbers.
  All monetary values MUST be in "${preferredCurrency}".
  Return JSON. Ensure the JSON is valid.`;

  const response = await ai.models.generateContent({
    model: "gemini-3-flash-preview",
    contents: prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: launchMathDeepDiveSchema,
      tools: [{ googleSearch: {} }]
    }
  });

  const text = response.text || '{}';
  try {
    return JSON.parse(text) as LaunchMathDeepDive;
  } catch (err) {
    console.error("JSON Parse Error in generateLaunchMathDeepDive:", err, text);
    throw new Error("Failed to parse launch math analysis. The AI response was malformed.");
  }
}

export async function serverGenerateSiteReconDeepDive(
  idea: BusinessIdea,
  location: string
): Promise<SiteReconDeepDive> {
  const ai = getAIClient();
  
  const prompt = `Act as a Senior Real Estate Strategist and Site Selection Expert. Perform an exhaustive 'Boots on the Ground' Site Recon report for the following venture:
  Venture: ${idea.title}
  Description: ${idea.description}
  Location: ${location}
  
  MANDATORY: Produce four distinct sections with extreme density (at least 300 words per section):
  1. Micro-Location Fit: Analyze the specific foot traffic and 'Anchor Businesses' in the district that will drive customers.
  2. Accessibility & Logistics: Detailed look at parking, public transit proximity, and delivery ease for suppliers.
  3. Demographic Alignment: Match the local resident profile (income level, age, habits) to the business model.
  4. Visual Landscape: Describe the 'vibe' of the street and why this specific aesthetic fits the brand.
  
  Use Google Search grounding to find actual local businesses, transit routes, and demographic data in ${location} to provide 'Human-Level' satisfaction.
  Return JSON. Ensure the JSON is valid.`;

  const response = await ai.models.generateContent({
    model: "gemini-3-flash-preview",
    contents: prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: siteReconDeepDiveSchema,
      tools: [{ googleSearch: {} }]
    }
  });

  const text = response.text || '{}';
  try {
    return JSON.parse(text) as SiteReconDeepDive;
  } catch (err) {
    console.error("JSON Parse Error in generateSiteReconDeepDive:", err, text);
    throw new Error("Failed to parse site recon analysis. The AI response was malformed.");
  }
}

export async function serverGenerateScalingStrategyDeepDive(
  idea: BusinessIdea,
  location: string
): Promise<ScalingStrategyDeepDive> {
  const ai = getAIClient();
  
  const prompt = `Act as a Senior Venture Strategist and Growth Architect. Perform an exhaustive '18-Month Scaling Strategy' for the following venture:
  Venture: ${idea.title}
  Description: ${idea.description}
  Location: ${location}
  
  MANDATORY STRUCTURE:
  1. The First 90 Days: Provide weekly milestones from 'Legal Incorporation' to 'Soft Launch.' Each milestone MUST have a detailed 'How-To' paragraph.
  2. Quarterly Scaling: Provide specific goals for Month 6, 12, and 18. Each goal MUST have a detailed 'How-To' paragraph.
  3. Trend Analysis: Use Google Search grounding to find 3 'Disruptive Technologies' or 'Consumer Shifts' specific to the ${idea.category} industry that will hit in 2026/2027.
  4. Pivot Points: Describe 2 scenarios where the business might need to change direction based on these trends.
  
  Each milestone, goal, and trend description MUST be high-density and actionable.
  Return JSON. Ensure the JSON is valid.`;

  const response = await ai.models.generateContent({
    model: "gemini-3-flash-preview",
    contents: prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: scalingStrategyDeepDiveSchema,
      tools: [{ googleSearch: {} }]
    }
  });

  const text = response.text || '{}';
  try {
    return JSON.parse(text) as ScalingStrategyDeepDive;
  } catch (err) {
    console.error("JSON Parse Error in generateScalingStrategyDeepDive:", err, text);
    throw new Error("Failed to parse scaling strategy analysis. The AI response was malformed.");
  }
}

export async function serverGenerateIntelligenceHub(idea: BusinessIdea, location: string): Promise<IntelligenceHubData> {
  const ai = getAIClient();
  const prompt = `You are a Market Intelligence Analyst. Generate a high-density intelligence report for this business idea:
  
  Title: ${idea.title}
  Category: ${idea.category}
  Location: ${location}
  
  Provide detailed analysis for:
  - Competitor Deep Dive: Top 3 competitors and their specific strengths/weaknesses.
  - Differentiation Engine: How this business will win.
  - Market Share Estimation: Realistic capture targets.
  - Seasonality Forecasting: Monthly demand fluctuations.
  - Gentrification Trajectory: Local area development trends.
  - Economic Cycle Resilience: How it handles downturns.
  - Persona Generator: 3 detailed customer archetypes.
  - Foot Traffic Patterns: Peak times and locations.
  - Sentiment Analysis: Local perception of this industry.
  
  Use Google Search Grounding for real-world data.
  Output MUST be valid JSON matching the schema.`;

  const response = await ai.models.generateContent({
    model: "gemini-3-flash-preview",
    contents: prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: intelligenceHubSchema,
      tools: [{ googleSearch: {} }]
    }
  });

  const text = response.text || '{}';
  try {
    return JSON.parse(text) as IntelligenceHubData;
  } catch (err) {
    console.error("JSON Parse Error in generateIntelligenceHub:", err, text);
    throw new Error("Failed to parse intelligence hub analysis.");
  }
}

export async function serverGenerateOperationsHub(idea: BusinessIdea, location: string): Promise<OperationsHubData> {
  const ai = getAIClient();
  const prompt = `You are an Operations Strategy Expert. Generate a high-density operational report for this business idea:
  
  Title: ${idea.title}
  Category: ${idea.category}
  Location: ${location}
  
  Provide detailed analysis for:
  - Scenario Modeling: Best/Worst case operational paths.
  - Investor Matchmaking: Types of investors to target.
  - Grant Scanner: Local/National grants available.
  - Labor Market Analytics: Hiring costs and availability in ${location}.
  - Supply Chain Mapping: Key suppliers and logistics.
  - Regulatory Navigator: Specific permits and licenses needed.
  - Performance Benchmarking: KPIs to track.
  - Expansion Scouting: Future location potential.
  - Exit Strategy Modeling: Potential acquisition targets.
  
  Use Google Search Grounding for real-world data.
  Output MUST be valid JSON matching the schema.`;

  const response = await ai.models.generateContent({
    model: "gemini-3-flash-preview",
    contents: prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: operationsHubSchema,
      tools: [{ googleSearch: {} }]
    }
  });

  const text = response.text || '{}';
  try {
    return JSON.parse(text) as OperationsHubData;
  } catch (err) {
    console.error("JSON Parse Error in generateOperationsHub:", err, text);
    throw new Error("Failed to parse operations hub analysis.");
  }
}

export async function serverGenerateCollaborationHub(idea: BusinessIdea, location: string): Promise<CollaborationHubData> {
  const ai = getAIClient();
  const prompt = `You are a Strategic Partnerships Advisor. Generate a high-density collaboration report for this business idea:
  
  Title: ${idea.title}
  Category: ${idea.category}
  Location: ${location}
  
  Provide detailed analysis for:
  - Stakeholder Sharing: Communication strategy.
  - Version Control: Documentation management.
  - Commentary Annotations: Feedback loops.
  - Algorithmic Transparency: How AI is used.
  - Bias Detection: Ensuring fairness.
  - Data Privacy Controls: Compliance with local laws.
  - CRM Connectors: Recommended software.
  - ERP Integrations: Operational systems.
  - API Access: Potential for technical expansion.
  - Rural Market Optimization: If applicable.
  - Full Localization: Cultural adjustments.
  - Advanced Offline Mode: Operational resilience.
  
  Use Google Search Grounding for real-world data.
  Output MUST be valid JSON matching the schema.`;

  const response = await ai.models.generateContent({
    model: "gemini-3-flash-preview",
    contents: prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: collaborationHubSchema,
      tools: [{ googleSearch: {} }]
    }
  });

  const text = response.text || '{}';
  try {
    return JSON.parse(text) as CollaborationHubData;
  } catch (err) {
    console.error("JSON Parse Error in generateCollaborationHub:", err, text);
    throw new Error("Failed to parse collaboration hub analysis.");
  }
}

export async function serverGenerateScrutiny(
  input: any,
  isPremium: boolean
): Promise<any> {
  const ai = getAIClient();
  const model = isPremium ? "gemini-3.1-pro-preview" : "gemini-3-flash-preview";
  
  const prompt = `You are a senior venture analyst conducting due diligence on a proposed business. Your output must be ruthlessly honest, data-backed, and actionable. No encouragement without evidence. No dismissal without explanation. 

Venture Description: ${input.ventureDescription}
Sector: ${input.targetSector}${input.otherSector ? ` (${input.otherSector})` : ''}
Location: ${input.targetLocation}
Capital Estimate: ${input.estimatedCapital} ${input.currency}
Founder Experience: ${input.experience}
Team Size: ${input.teamSize}
Launch Timeline: ${input.launchTimeline}
Existing Assets: ${input.existingAssets}

Available intelligence includes location data from Site Recon, competitor data from Entity Spotting, market data from sector benchmarks, regulatory data from local requirements, and economic data from regional indicators.

Tasks:
1. Generate a Fragility Index from 0 to 100 as a composite score where higher means more critical gaps, weighted as:
   - demand-location fit: 30%
   - capital adequacy: 25%
   - regulatory risk: 20%
   - competitive density: 15%
   - founder-market fit: 10%

2. Generate Critical Gaps (max 5 entries): gap name, severity (fatal, major, minor), user assumption identified, data contradicting the assumption, and quantified gap where possible.

3. Generate Hidden Assumptions Flagged (max 5): implicit beliefs the user holds that are unsupported or contradicted by market data.

4. Generate Viable Pivots (3-5): alternative approaches preserving core intent while eliminating identified gaps, with estimated capital, timeline, and confidence score.

5. Recommended Next Step: a single action item with specific hub routing (launchpad, discovery, or optimize).

ThinkingLevel: HIGH. Temperature: 0.1.
Output MUST be valid JSON matching the schema. Use Google Search grounding.`;

  const response = await ai.models.generateContent({
    model: model,
    contents: prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: scrutinySchema,
      thinkingConfig: { thinkingLevel: ThinkingLevel.HIGH },
      temperature: 0.1,
      tools: [{ googleSearch: {} }]
    }
  });

  const text = response.text || '{}';
  try {
    const result = JSON.parse(text);
    return {
      ...result,
      generatedAt: Date.now(),
      modelVersion: model
    };
  } catch (err) {
    console.error("JSON Parse Error in generateScrutiny:", err, text);
    throw new Error("Failed to parse scrutiny analysis.");
  }
}

export async function serverGenerateMarketAnalysis(idea: BusinessIdea, location: string): Promise<string> {
  const ai = getAIClient();
  const ventureTitle = idea.title || 'Target Venture';
  const ventureCategory = idea.category || 'General';
  const ventureDesc = idea.description || 'Commercial operational enterprise in this field.';
  const ventureLoc = location || idea.locationName || 'the target area';

  const prompt = `You are a Senior Strategic Market Analyst. Generate an in-depth, rigorous market opportunity analysis strictly for this venture:
  
  Venture Title: ${ventureTitle}
  Category / Sector: ${ventureCategory}
  Location / District: ${ventureLoc}
  Venture Blueprint & Core Offering: ${ventureDesc}
  
  STRICT ACCURACY & CONTEXTUAL LOCK MANDATES:
  1. ABSOLUTE CONTEXTUAL LOCK: Your entire analysis MUST focus 100% exclusively on "${ventureTitle}" and its specific offering: (${ventureDesc}).
  2. STRICTLY FORBIDDEN: Do NOT drift, cross-wire, hallucinate, or substitute unrelated business sectors (e.g., commercial real estate, janitorial/facility sanitization, fintech, food & beverage, etc. unless this venture explicitly is that business). Every single sentence must directly evaluate this exact venture in ${ventureLoc}.
  3. STRUCTURE: Write 3-4 substantial, dense analytical narrative paragraphs covering:
     - Paragraph 1 (Market Landscape & Local Demand): Hyper-local market conditions, specific target customer personas, and demand drivers in ${ventureLoc} for "${ventureTitle}".
     - Paragraph 2 (Market Gap & Opportunity): The exact service or supply void in the area, unmet customer pain points, and why current solutions are inadequate.
     - Paragraph 3 (Competitive Dynamics & Incumbent Behavior): Local competition landscape, pricing and margin opportunities, and defensible competitive advantages.
     - Paragraph 4 (Strategic Timing & Execution Outlook): Timing rationale, operational milestones, regulatory considerations, and commercial growth runway.
  4. FORMAT: Return pure flowing analytical paragraphs only. No bullet points, no markdown headings, no JSON code blocks, no preamble, and no first-person ("I", "we"). Write in authoritative, location-grounded third-person prose.`;

  const response = await ai.models.generateContent({
    model: "gemini-3-flash-preview",
    contents: prompt,
    config: {
      tools: [{ googleSearch: {} }]
    }
  });

  return response.text || "Market analysis unavailable.";
}

export async function serverGenerateLivingSwot(
  idea: BusinessIdea, 
  location: string, 
  contextData: {
    intelligence?: IntelligenceHubData;
    operations?: OperationsHubData;
    performance?: any;
  }
): Promise<any> {
  const ai = getAIClient();
  
  const prompt = `Act as a Strategic Intelligence Engine. Generate a "Living SWOT" analysis for:
  Venture: ${idea.title}
  Category: ${idea.category}
  Location: ${location}

  Contextual Data:
  - Intelligence: ${JSON.stringify(contextData.intelligence || {})}
  - Operations: ${JSON.stringify(contextData.operations || {})}
  - Performance: ${JSON.stringify(contextData.performance || {})}

  MANDATORY: Populate four dynamic quadrants based on these specific data sources:
  1. Strengths: Site Recon advantages, Differentiation Engine positioning, and actual performance exceeding projections.
  2. Weaknesses: LaunchMath capital constraints, RiskGuard vulnerabilities, Labor Analytics gaps, and operational underperformance.
  3. Opportunities: Customer Intelligence unmet needs, Grant Scanner incentives, Seasonality demand windows, and Sentiment Analysis positive trends.
  4. Threats: Competitor pricing pressure, Gentrification rent increases, Economic Cycle stress-test failures, and new rival entry detected.

  For each element, provide:
  - id: unique string
  - text: clear, high-impact insight
  - score: 0-100 (impact magnitude)
  - confidence: 0-1 (data reliability)
  - source: specific module name (e.g., 'Site Recon', 'RiskGuard')
  - status: 'new', 'updated', or 'stable'
  - impact: 0-1 (strategic weight)

  Also provide a strategic narrative summarizing the current state.
  Use Google Search Grounding for real-time market/competitor data.
  Return JSON.`;

  const response = await ai.models.generateContent({
    model: "gemini-3-flash-preview",
    contents: prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: livingSwotSchema,
      tools: [{ googleSearch: {} }]
    }
  });

  const text = response.text || '{}';
  try {
    return JSON.parse(text);
  } catch (err) {
    console.error("JSON Parse Error in generateLivingSwot:", err, text);
    throw new Error("Failed to parse Living SWOT analysis.");
  }
}

export async function serverRunAnalysis(
  featureType: FeatureType | string,
  location: string,
  coords: { lat: number; lng: number } | null,
  budgetLimit?: number,
  skills?: string,
  targetBusiness?: string,
  preferredCurrency: string = 'USD',
  _discoveryCountry: string = 'USA'
): Promise<AnalysisResult> {
  const ai = getAIClient();
  
  let masterPrompt = "";
  const isDiscovery = featureType === FeatureType.ReverseDiscovery || featureType === FeatureType.DensityScout;
  const budgetPrompt = budgetLimit ? `STRICT CONSTRAINT: All business opportunities MUST be launchable within an initial capital budget of ${budgetLimit} ${preferredCurrency}. Ensure the LaunchMath "startupCost" values reflect this limit.` : '';

  if (featureType === FeatureType.ReverseDiscovery) {
    const searchFocus = targetBusiness ? 
      `Identify the prevalence and local context of "${targetBusiness}" in "${location}". Focus on why this niche exists here and what defines its presence.` : 
      `What is this neighborhood known for? Discover distinctive character hallmarks and niche industries of "${location}".`;
    
    masterPrompt = `Act as a Cultural & Business Intelligence Engine. Reverse Discovery for: "${location}". 
    ${searchFocus}
    Identify distinctive character hallmarks, cultural products, or underserved identity gaps. 
    Focus on rapid, high-impact factual identification.
    For each idea, include:
    1. Specific "siteRecommendations" (3 pinpoints).
    2. "GapFinder" competition analysis.
    3. Find 6-8 REAL businesses in "spottedEntities".
    STRICT: DO NOT include financial projections, startup costs, ROI, risk scores, operational roadmaps, or market demand analysis.
    FOR EACH IDEA: Include 1-2 relevant web URLs in the "sources" field.`;
  } else if (featureType === FeatureType.DensityScout) {
    masterPrompt = `Act as a Geographic & Strategic Analyst. Mapping clusters for: "${targetBusiness || 'Distinctive Niche'}" in "${location}". 
    Identify geographic hotspots where this presence is most dense. 
    CRITICAL: Provide 10-12 "heatmapData" points (lat, lng, weight) representing the real-world density.
    Focus on rapid, high-impact factual identification.
    For each idea, include:
    1. Specific "siteRecommendations" (3 pinpoints).
    2. "GapFinder" competition analysis.
    3. Find 6-8 REAL businesses for "spottedEntities".
    STRICT: DO NOT include financial projections, startup costs, ROI, risk scores, operational roadmaps, or market demand analysis.
    FOR EACH IDEA: Include 1-2 relevant web URLs in the "sources" field.`;
  } else {
    masterPrompt = `Act as a Senior Venture Architect. Perform a Hyper-Specific Feasibility Study for "${targetBusiness || 'High-Viability Opportunities'}" in "${location}". 
    MANDATORY: Provide a comprehensive high-level feasibility summary.
    All monetary values MUST be in "${preferredCurrency}".
    ${budgetPrompt}
    For each idea, include:
    1. Full "LaunchMath" (fullPath and leanPath).
    2. Comprehensive "RiskGuard" analysis.
    3. Specific "siteRecommendations" (3 pinpoints).
    4. "GapFinder" competition analysis.
    5. Find 6-8 REAL businesses for "spottedEntities".
    FOR EACH IDEA: Include 3-5 relevant web URLs from your search grounding in the "sources" field.`;
  }

  const prompt = `${masterPrompt} Return JSON. Use Google Search grounding to ensure factual accuracy. For Discovery tasks, ensure all output is descriptive of identity, not evaluative of profit. For each idea generated, you MUST provide at least one source URL. Ensure heatmapData points are geographically diverse within the target region.`;

  const response = await ai.models.generateContent({
    model: "gemini-3-flash-preview",
    contents: prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: analysisSchema,
      tools: [{ googleSearch: {} }],
      thinkingConfig: isDiscovery ? { thinkingLevel: ThinkingLevel.LOW } : undefined
    }
  });

  const text = response.text || '{}';
  try {
    const result = JSON.parse(text) as AnalysisResult;
    
    result.ideas = (result.ideas || []).map((idea, idx) => {
      const title = idea.title || `Venture ${idx + 1}`;
      const loc = idea.locationName || location || '';
      const uniqueId = generateVentureId(title, loc, idea.id);

      return {
        ...idea,
        id: uniqueId,
        locationName: idea.locationName || location || '',
        hubOrigin: isDiscovery ? 'discovery' : 'launchpad'
      };
    });

    const chunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks || [];
    const sources = chunks.filter((c: any) => c.web).map((c: any) => ({
      title: c.web.title || 'Source',
      uri: c.web.uri
    }));

    return { ...result, sources };
  } catch (err) {
    console.error("JSON Parse Error in runAnalysis:", err, text);
    throw new Error("Failed to parse intelligence report. The AI response was malformed.");
  }
}

export async function serverGenerateGrassrootsVentures(input: GrassrootsInput): Promise<AnalysisResult> {
  const ai = getAIClient();

  const currency = input.preferredCurrency || 'USD';
  const budgetPrompt = input.budgetLimit ? `STRICT CONSTRAINT: All venture ideas MUST be launchable within a maximum initial capital of ${input.budgetLimit} ${currency}.` : '';

  const prompt = `Act as a Global Grassroots Venture Architect & Frugal Innovation Strategist.
  Analyze the user's observational qualitative surroundings in "${input.location || 'Global Target Location'}":
  
  ENVIRONMENT & SURROUNDINGS:
  ${input.surroundingsDescription}
  
  AVAILABLE LOCAL RESOURCES & SKILLS:
  ${input.availableResources}
  
  COMMUNITY PAIN POINTS & UNMET GAPS:
  ${input.communityGaps}
  
  ${budgetPrompt}
  All monetary estimates MUST be in "${currency}".

  CROSS-REFERENCE TASK:
  1. Synthesize 3-4 viable, grounded, high-viability grassroots venture ideas specifically engineered for this environment.
  2. Cross-reference the user's qualitative input with REAL-WORLD GLOBAL BENCHMARKS & FRUGAL INNOVATION CASE STUDIES (e.g. successful off-grid solar, decentralized agro-processing, micro-logistics, or community services in developing or underserved global markets).
  3. In the "culturalSignificance" field for each idea, explicitly reference a REAL GLOBAL BENCHMARK CASE STUDY or benchmark precedent.
  4. In the "description" field, explain exactly why this business matches the user's described surroundings (e.g., road conditions, foot traffic, power availability).
  5. Provide full LaunchMath (fullPath, leanPath), estimatedStartupCost, riskLevel, and 3 pinpoint "siteRecommendations".
  6. Include 1-2 web sources for each idea from Google Search grounding verifying similar global benchmarks.
  
  Return JSON matching the analysis schema.`;

  const response = await ai.models.generateContent({
    model: "gemini-3-flash-preview",
    contents: prompt,
    config: {
      responseMimeType: "application/json",
      responseSchema: analysisSchema,
      tools: [{ googleSearch: {} }]
    }
  });

  const text = response.text || '{}';
  try {
    const result = JSON.parse(text) as AnalysisResult;
    
    result.ideas = (result.ideas || []).map(idea => ({
      ...idea,
      hubOrigin: 'discovery',
      tags: Array.from(new Set([...(idea.tags || []), 'Grassroots', 'Observational', 'Frugal Innovation']))
    }));

    const chunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks || [];
    const sources = chunks.filter((c: any) => c.web).map((c: any) => ({
      title: c.web.title || 'Source',
      uri: c.web.uri
    }));

    return { ...result, sources };
  } catch (err) {
    console.error("JSON Parse Error in generateGrassrootsVentures:", err, text);
    throw new Error("Failed to synthesize grassroots ventures. Please check input parameters.");
  }
}

// ==========================================
// 6. ISOMORPHIC UNIFIED EXPORTS
// ==========================================
// When running in Node.js on the server: calls the server AI functions directly.
// When running in the Browser: calls the backend HTTP proxy endpoints.

export async function getLocationSuggestions(
  area: string,
  sector: string,
  country?: string,
  featureType?: string,
  coords?: { lat: number; lng: number } | null,
  groundingSynthesis?: string
): Promise<any> {
  if (isServer) {
    return serverGetLocationSuggestions(area, sector, country);
  }
  try {
    const body: Record<string, any> = {
      area,
      sector: sector || '',
      featureType: featureType || 'GapFinder',
      coords,
      groundingSynthesis,
    };
    if (country) body.country = country;
    return await post('/api/suggestions', body);
  } catch (err) {
    console.warn('Location suggestion fetch notice:', err);
    return [];
  }
}

export async function resolveCountryFromCoords(lat: number, lng: number): Promise<string> {
  if (isServer) return serverResolveCountryFromCoords(lat, lng);
  return 'USA';
}

export async function resolveLocationFromCoords(lat: number, lng: number): Promise<string> {
  if (isServer) return serverResolveLocationFromCoords(lat, lng);
  return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
}

export async function geocodeLocation(query: string): Promise<any> {
  if (isServer) {
    return serverGeocodeLocation(query);
  }
  return mapsGeocode(query);
}

export async function runAnalysis(
  featureType: FeatureType | string,
  location: string,
  coords: { lat: number; lng: number } | null,
  budgetLimit?: number,
  skills?: string,
  targetBusiness?: string,
  preferredCurrency: string = 'USD',
  discoveryCountry: string = 'USA',
  groundingSynthesis?: string
): Promise<any> {
  if (isServer) {
    return serverRunAnalysis(
      featureType,
      location,
      coords,
      budgetLimit,
      skills,
      targetBusiness,
      preferredCurrency,
      discoveryCountry
    );
  }
  const body: Record<string, any> = {
    featureType,
    location,
    coords,
    budgetLimit,
    skills,
    targetBusiness,
    preferredCurrency,
    discoveryCountry,
    groundingSynthesis,
  };
  return post('/api/analyze', body);
}

export async function compareOpportunities(
  ideas: any[],
  preferredCurrency: string = 'USD'
): Promise<any> {
  if (isServer) {
    return serverCompareOpportunities(ideas, preferredCurrency);
  }
  return post('/api/compare', { ideas, preferredCurrency });
}
export const compareIdeas = compareOpportunities;

export async function generateRiskGuardDeepDive(
  idea: any,
  location: string,
  preferredCurrency: string = 'USD'
): Promise<any> {
  if (isServer) {
    return serverGenerateRiskGuardDeepDive(idea, location, preferredCurrency);
  }
  return post('/api/deep-dive/risk', { idea, location, preferredCurrency });
}

export async function generateLaunchMathDeepDive(
  idea: any,
  location: string,
  preferredCurrency: string = 'USD'
): Promise<any> {
  if (isServer) {
    return serverGenerateLaunchMathDeepDive(idea, location, preferredCurrency);
  }
  return post('/api/deep-dive/launch', { idea, location, preferredCurrency });
}

export async function generateSiteReconDeepDive(
  idea: any,
  location: string
): Promise<any> {
  if (isServer) {
    return serverGenerateSiteReconDeepDive(idea, location);
  }
  return post('/api/deep-dive/site', { idea, location });
}

export async function generateScalingStrategyDeepDive(
  idea: any,
  location: string
): Promise<any> {
  if (isServer) {
    return serverGenerateScalingStrategyDeepDive(idea, location);
  }
  return post('/api/deep-dive/scaling', { idea, location });
}

export async function generateIntelligenceHub(idea: any, location: string): Promise<any> {
  if (isServer) {
    return serverGenerateIntelligenceHub(idea, location);
  }
  return post('/api/hub/intelligence', { idea, location });
}

export async function generateOperationsHub(idea: any, location: string): Promise<any> {
  if (isServer) {
    return serverGenerateOperationsHub(idea, location);
  }
  return post('/api/hub/operations', { idea, location });
}

export async function generateCollaborationHub(idea: any, location: string): Promise<any> {
  if (isServer) {
    return serverGenerateCollaborationHub(idea, location);
  }
  return post('/api/hub/collaboration', { idea, location });
}

export async function generateScrutiny(input: any, isPremium: boolean = false): Promise<any> {
  if (isServer) {
    return serverGenerateScrutiny(input, isPremium);
  }
  return post('/api/scrutiny', { input, isPremium });
}
export const runScrutinyAnalysis = generateScrutiny;

export async function generateMarketAnalysis(idea: any, location: string): Promise<string> {
  if (isServer) {
    return serverGenerateMarketAnalysis(idea, location);
  }
  const data = await post<any>('/api/market-analysis', {
    idea: {
      title: idea.title || idea.name || 'Unnamed',
      description: idea.description || '',
      category: idea.category || 'General',
      locationName: idea.locationName || location || '',
    },
    location,
  });
  const text = typeof data === 'string' ? data : (data?.analysis || data?.text || '');
  return text || 'Market analysis unavailable';
}
export const analyzeMarket = generateMarketAnalysis;

export async function generateLivingSwot(idea: any, location: string, contextData: any): Promise<any> {
  if (isServer) {
    return serverGenerateLivingSwot(idea, location, contextData);
  }
  const raw = await generateScrutiny({
    ventureDescription: idea.description,
    targetSector: idea.category,
    targetLocation: location,
    ...contextData
  }, false);

  const now = Date.now();
  const toElements = (arr: any[]): any[] => {
    if (!Array.isArray(arr)) return [];
    return arr.map((item: any, idx: number) => {
      if (typeof item === 'string') {
        return {
          id: `swot-${idx}-${now}`,
          text: item,
          score: 50 + Math.floor(Math.random() * 30),
          confidence: 0.65 + Math.random() * 0.25,
          source: 'AI Analysis',
          lastUpdated: now,
          status: 'new',
          impact: 0.4 + Math.random() * 0.4
        };
      }
      return {
        id: item.id || `swot-${idx}-${now}`,
        text: item.text || String(item),
        score: typeof item.score === 'number' ? item.score : 50,
        confidence: typeof item.confidence === 'number' ? item.confidence : 0.7,
        source: item.source || 'AI Analysis',
        lastUpdated: item.lastUpdated || now,
        status: item.status || 'new',
        impact: typeof item.impact === 'number' ? item.impact : 0.5
      };
    });
  };

  const swotSource = (raw as any).swot || raw;
  return {
    id: `swot-${idea.id || 'temp'}-${now}`,
    ventureId: idea.id || 'temp',
    strengths: toElements(swotSource.strengths || []),
    weaknesses: toElements(swotSource.weaknesses || []),
    opportunities: toElements(swotSource.opportunities || []),
    threats: toElements(swotSource.threats || []),
    narrative: (raw as any).summary || (raw as any).narrative || '',
    lastUpdated: now,
    version: 1
  };
}

export async function generateGrassrootsVentures(input: GrassrootsInput): Promise<any> {
  if (isServer) {
    return serverGenerateGrassrootsVentures(input);
  }
  const targetBusiness = `Grassroots Frugal Venture: ${input.surroundingsDescription}. Available Resources: ${input.availableResources}. Community Pain Points: ${input.communityGaps}`;
  return runAnalysis(
    'GrassrootsIdeator',
    input.location || 'Global Target Location',
    null,
    input.budgetLimit || 1500,
    input.availableResources,
    targetBusiness,
    input.preferredCurrency || 'USD',
    'USA',
    input.surroundingsDescription
  );
}
